import { getBorrowerValuesToSend } from 'app/pages/loanApplication/tabs/borrowers/EditEntityModal/Content/Content.utils';
import { buildPostClosingRequestBody } from 'app/pages/loanApplication/tabs/postClosing/EditDrawer/EditDrawer.utils';
import {
  getContactsBodyRequestAction,
  getLoanAndLockRequestBody,
  getPropertyAndLoanRequestBody,
  normalizeLoanAndLockPendingChangesAction,
  normalizePropertyAndLoanPendingChangesAction,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/Content.utils';
import _ from 'lodash';

import { logger } from 'utils/logger';

import {
  bulkUpdateBorrowersAction,
  bulkUpdateLoanContactsAction,
  editClosingInformationAction,
  editFundingInformationAction,
  editLoanAndLockSummaryAction,
  editPostClosingAction,
  editPropertyAndLoanSummaryAction,
} from 'store/loanApplications/actions';

import {
  buildClosingSaveRequest,
  buildFundingSaveRequest,
  pickChangedEntityValues,
} from './LoanEditorCustomFormDrawer.closingFunding.save';
import {
  CLOSING_FORM_PATH,
  DRAWER_MESSAGES,
  FUNDING_FORM_PATH,
  POST_CLOSING_FORM_PATH,
} from './LoanEditorCustomFormDrawer.constants';
import {
  buildLoanAndLockEntitySideData,
  buildPropertyEntitySideData,
} from './LoanEditorCustomFormDrawer.utils';

export const buildLoanAndLockMergedPendingChanges = ({
  loanAndLockResponseData,
  remoteLoanAndLockPendingChanges,
  formPendingChanges,
  applicationData,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  housingFinanceAgenciesOptions,
  isLoanNoPpeEngineType,
}) => {
  if (!loanAndLockResponseData) {
    return {};
  }

  return normalizeLoanAndLockPendingChangesAction({
    initialPendingChanges: remoteLoanAndLockPendingChanges ?? {},
    formPendingChanges,
    applicationData,
    entitySideData: {},
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    shouldShowLoanRolesSection: false,
    shouldShowLoanTagsSection: false,
    shouldShowProfitAndLossSection: false,
    shouldShowOBCustomFieldsSection: false,
    initialEntitySideData: {},
    housingFinanceAgenciesOptions,
    isLoanNoPpeEngineType,
  });
};

export const buildPropertyMergedPendingChanges = ({
  propertyResponseData,
  remotePropertyPendingChanges,
  formPendingChanges,
  applicationData,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  housingFinanceAgenciesOptions,
  loanType,
  isLoanNoPpeEngineType,
}) => {
  if (!propertyResponseData) {
    return {};
  }

  return normalizePropertyAndLoanPendingChangesAction({
    initialPendingChanges: remotePropertyPendingChanges ?? {},
    formPendingChanges,
    applicationData,
    entitySideData: {},
    initialEntitySideData: {},
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    shouldShowLoanRolesSection: false,
    shouldShowLoanTagsSection: false,
    shouldShowProfitAndLossSection: false,
    shouldShowOBCustomFieldsSection: false,
    housingFinanceAgenciesOptions,
    loanType,
    isLoanNoPpeEngineType,
    newRecords: [],
    newAddedInitialRecords: [],
    deletedRecords: [],
  });
};

const withStoredRelation = ({ borrowerForm, storedRelation }) =>
  _.isNil(borrowerForm.relation?.value) && !_.isNil(storedRelation)
    ? { ...borrowerForm, relation: { value: storedRelation } }
    : borrowerForm;

export const buildBorrowerSaveRequest = ({
  applicationId,
  formState,
  selectedBorrowerId,
  applicationData,
  isBplLoanPurposeType,
}) => ({
  applicationId,
  body: getBorrowerValuesToSend(
    {
      [selectedBorrowerId]: withStoredRelation({
        borrowerForm: formState?.[selectedBorrowerId] ?? {},
        storedRelation: applicationData?.[selectedBorrowerId]?.relation,
      }),
    },
    applicationData,
    isBplLoanPurposeType,
    {},
  ),
});

export const buildEntitySavePromises = ({
  dispatch,
  applicationId,
  loanId,
  formState,
  applicationData,
  loanType,
  isLoanNoPpeEngineType,
  isBplLoanPurposeType,
  loanAndLockResponseData,
  selectedBorrowerId,
  selectedContactId,
  loanAndLockEditedFieldPaths,
  loanAndLockHiddenFieldPaths,
  hasPropertyInfoChanges,
  hasBorrowerChanges,
  hasContactChanges,
  postClosing,
  postClosingSectionKeys,
  hasPostClosingChanges,
  formPendingChanges,
  closingSaveContext,
  hasClosingChanges,
  storedFundingData,
  hasFundingChanges,
}) => {
  const loanAndLockSavePromise = loanAndLockEditedFieldPaths?.size
    ? dispatch(
        editLoanAndLockSummaryAction({
          applicationId,
          loanId,
          ...getLoanAndLockRequestBody({
            form: _.omit(formState, [...(loanAndLockHiddenFieldPaths ?? [])]),
            applicationData,
            entitySideData: buildLoanAndLockEntitySideData(loanAndLockResponseData),
            isLoanNoPpeEngineType,
            withExtensionLimitOverride: false,
            isExternalRequestType: false,
            isLockExtensionsV2: false,
            isProfitAndLossManageAllowed: false,
            customFields: [],
            shouldShowOBCustomFieldsSection: false,
            editedLoanInfoFieldPaths: new Set([
              ...loanAndLockEditedFieldPaths,
              ...(loanAndLockHiddenFieldPaths ?? []),
            ]),
          }),
        }),
      )
    : Promise.resolve(null);

  const propertySavePromise = hasPropertyInfoChanges
    ? dispatch(
        editPropertyAndLoanSummaryAction({
          applicationId,
          loanId,
          ...getPropertyAndLoanRequestBody({
            form: formState,
            entitySideData: buildPropertyEntitySideData(applicationData),
            applicationData,
            loanType,
          }),
        }),
      )
    : Promise.resolve(null);

  const borrowerSavePromise = hasBorrowerChanges
    ? dispatch(
        bulkUpdateBorrowersAction(
          buildBorrowerSaveRequest({
            applicationId,
            formState,
            selectedBorrowerId,
            applicationData,
            isBplLoanPurposeType,
          }),
        ),
      )
    : Promise.resolve(null);

  const contactSavePromise =
    hasContactChanges && !_.isNil(selectedContactId)
      ? dispatch(
          bulkUpdateLoanContactsAction({
            applicationId,
            ...getContactsBodyRequestAction({
              form: { [selectedContactId]: formState?.[selectedContactId] ?? {} },
              deletedRecords: [],
            }),
          }),
        )
      : Promise.resolve(null);

  const postClosingSavePromise = hasPostClosingChanges
    ? dispatch(
        editPostClosingAction(
          buildPostClosingRequestBody({
            loanId,
            formData: formState?.[POST_CLOSING_FORM_PATH] ?? {},
            postClosing,
            sectionKeys: postClosingSectionKeys,
          }),
        ),
      )
    : Promise.resolve(null);

  const closingSavePromise = hasClosingChanges
    ? dispatch(
        editClosingInformationAction({
          applicationId,
          ...buildClosingSaveRequest({
            ...closingSaveContext,
            changedValues: pickChangedEntityValues({
              formState,
              formPendingChanges,
              formPath: CLOSING_FORM_PATH,
            }),
          }),
        }),
      )
    : Promise.resolve(null);

  const fundingSavePromise = hasFundingChanges
    ? dispatch(
        editFundingInformationAction({
          loanId,
          ...buildFundingSaveRequest({
            storedFundingData,
            changedValues: pickChangedEntityValues({
              formState,
              formPendingChanges,
              formPath: FUNDING_FORM_PATH,
            }),
          }),
        }),
      )
    : Promise.resolve(null);

  return {
    loanAndLockSavePromise,
    propertySavePromise,
    borrowerSavePromise,
    contactSavePromise,
    postClosingSavePromise,
    closingSavePromise,
    fundingSavePromise,
  };
};

export const logRejectedResults = settledResults => {
  const rejectedResults = settledResults.filter(result => result.status === 'rejected');

  rejectedResults.forEach(result => logger.error(result.reason));

  return rejectedResults;
};

export const logAndDetectSaveRejection = saveResults => !!logRejectedResults(saveResults).length;

export const getSaveValidationErrorMessage = ({ isLoanFormValid, areCustomFieldsValid }) =>
  isLoanFormValid && areCustomFieldsValid ? null : DRAWER_MESSAGES.INVALID_FIELDS_FOR_SAVE;

export const checkIsSaveDisabled = ({
  isLoading,
  isSaving,
  hasAnyChanges,
  hasAnyEntityData,
  requiresBorrowerSelection,
  requiresContactSelection,
  requiresClosingCompanySelection,
}) =>
  isLoading ||
  isSaving ||
  !hasAnyChanges ||
  !hasAnyEntityData ||
  requiresBorrowerSelection ||
  requiresContactSelection ||
  requiresClosingCompanySelection;
